import { prepareTranscriptionSegments, type AudioSegment } from "./audioSegments";

export type TranscriptionResult = {
  text: string;
  segments: AudioSegment[];
};

type VoiceResponse = {
  text?: string;
  transcription?: string;
  transcript?: string;
  language?: string;
  duration?: number;
};

export async function transcribeRecordingBlob(blob: Blob): Promise<TranscriptionResult> {
  const segments = await prepareTranscriptionSegments(blob);
  const texts: string[] = [];

  for (const segment of segments) {
    const text = await transcribeAudioSegment(segment);
    if (text) texts.push(text);
  }

  return { text: texts.reduce(mergeTranscriptText, ""), segments };
}

export async function transcribeAudioSegment(segment: AudioSegment): Promise<string> {
  const form = new FormData();
  form.append("audio", segment.blob, `segment-${segment.index + 1}.wav`);
  form.append("language", "auto");

  const res = await fetch("/api/transcribe", {
    method: "POST",
    body: form,
  });

  if (!res.ok) {
    const detail = await res.text().catch(() => "");
    throw new Error(`Transcription failed (${res.status}): ${detail || res.statusText}`);
  }

  const json = (await res.json()) as VoiceResponse;
  return (json.text ?? json.transcription ?? json.transcript ?? "").trim();
}

export function mergeTranscriptText(existing: string, next: string): string {
  const a = existing.trim();
  const b = next.trim();
  if (!a) return b;
  if (!b) return a;

  const aLower = a.toLowerCase();
  const bLower = b.toLowerCase();
  if (aLower.endsWith(bLower)) return a;
  if (bLower.startsWith(aLower)) return b;

  const maxOverlap = Math.min(80, a.length, b.length);
  for (let len = maxOverlap; len >= 12; len--) {
    if (a.slice(-len).toLowerCase() === b.slice(0, len).toLowerCase()) {
      return `${a}${b.slice(len)}`.trim();
    }
  }

  const needsSpace = !/[\s\n]$/.test(a) && !/^[,.;:!?)]/.test(b);
  return `${a}${needsSpace ? " " : ""}${b}`.trim();
}
